import { useIsSidebarIAEnabled } from '@/features/flags';
import { createResizeObserverWithRAF } from '@/utils/resizeObserverWithRAF';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  FOOTER_MORE_TEST_ID,
  FOOTER_RAIL_GAP_PX,
  FOOTER_RAIL_IA_ONLY_IDS,
  FOOTER_RAIL_TEST_IDS,
  FOOTER_RAIL_TILE_ESTIMATE_PX,
  type FooterRailId,
} from './footer-rail.constants';

export interface FooterRailMeasurement {
  /** Fixed by the window, not by the footer. */
  availableHeight: number;
  /** chrome + full primary content + pinned footer — each invariant to the collapse decision. */
  reservedHeight: number;
  /** Entries survive an item moving into More (an unmounted item can't be re-measured). */
  heights: ReadonlyMap<FooterRailId, number>;
  moreHeight: number;
  /** Until true, render everything uncollapsed. */
  hasMeasured: boolean;
}

const EMPTY_MEASUREMENT: FooterRailMeasurement = {
  availableHeight: 0,
  reservedHeight: 0,
  heights: new Map(),
  moreHeight: FOOTER_RAIL_TILE_ESTIMATE_PX,
  hasMeasured: false,
};

interface MeasurementSnapshot {
  measurement: FooterRailMeasurement;
  /** The pinned-footer remainder, cached separately because a seeded mount has no
   * expanded pass to recompute it from. */
  footerPinned: number;
}

// Every page navigation remounts the whole sidebar (layouts aren't persistent), so
// this cache lets a remounted provider seed the last trustworthy measurement and
// paint a collapsed footer immediately, instead of replaying the "render uncollapsed
// → measure → collapse" bootstrap (the flicker). Module-level so it survives the
// remount; never written on the server (measure() is DOM-driven), so SSR and first
// paint fall through to EMPTY_MEASUREMENT and match. Keyed by locale because cached
// item heights are locale-specific. Bounded by the number of locales visited.
const measurementCache = new Map<string, MeasurementSnapshot>();

/** Test-only: reset the cross-mount cache between cases. */
export const __resetMeasurementCache = () => measurementCache.clear();

const SIDEBAR_CONTAINER_SELECTOR = '[data-testid="sidebar-container"]';
const SIDEBAR_CONTENT_SELECTOR = '[data-sidebar="content"]';

/** offsetHeight plus vertical margins (footer items are spaced with margins). */
export const outerHeight = (element: HTMLElement): number => {
  if (element.offsetHeight === 0) return 0;
  const style = getComputedStyle(element);
  return (
    element.offsetHeight +
    (parseFloat(style.marginTop) || 0) +
    (parseFloat(style.marginBottom) || 0)
  );
};

export const sumChildOuterHeights = (parent: Element): number => {
  let total = 0;
  for (const child of Array.from(parent.children)) {
    if (child instanceof HTMLElement) total += outerHeight(child);
  }
  return total;
};

export const heightsEqual = (
  a: ReadonlyMap<FooterRailId, number>,
  b: ReadonlyMap<FooterRailId, number>
): boolean => {
  if (a.size !== b.size) return false;
  for (const [id, height] of a) {
    if (b.get(id) !== height) return false;
  }
  return true;
};

/**
 * Measures the footer overflow budget so every input to the split is invariant to
 * the collapse decision (a content-sized footer would otherwise strobe: collapsing
 * an item shrinks the footer, grows the flex-1 primary rail, feeds back in). The
 * two traps that make that hard are handled in `measure()` below: measuring primary
 * content off the content-sized child (not `scrollHeight` on the flex-1 box), and
 * recomputing the pinned footer only while expanded.
 *
 * `measure()`'s arithmetic is covered by a stubbed-geometry test; consumers of the
 * hook still mock it wholesale (jsdom reports 0 for real geometry).
 */
export function useFooterRailMeasure(enabled: boolean) {
  const isSidebarIAEnabled = useIsSidebarIAEnabled();
  const { locale } = useRouter();
  const localeKey = locale ?? '';
  // Seed from the last mount's measurement so a post-navigation remount starts
  // collapsed. Geometry is viewport-stable across a client navigation, so it's a
  // valid starting point; this mount's own measure pass re-confirms it.
  const seed = measurementCache.get(localeKey);

  const [footerElement, setFooterElement] = useState<HTMLElement | null>(null);
  const [measurement, setMeasurement] = useState<FooterRailMeasurement>(
    () => seed?.measurement ?? EMPTY_MEASUREMENT
  );
  // Seeded so the first measure() (before items are in the DOM) can't wipe the
  // cached item heights — measure() is additive and copies this ref.
  const heightsRef = useRef<Map<FooterRailId, number>>(
    new Map(seed?.measurement.heights)
  );
  // Only updated on an expanded pass, so collapsing an item can't feed back in.
  const footerPinnedRef = useRef(seed?.footerPinned ?? 0);
  // Rendered collapsible sum of the previous pass. When a tile disappears
  // (collapsed), the sum shrinks — that is the collapse signal, and the pinned
  // reserve must NOT be re-derived from the shrunken footer, or the freed row
  // re-inflates the budget and the next item needs extra window shrink to fold.
  // Only a full-extent pass (sum grew) recomputes the reserve.
  const renderedCollapsibleSumRef = useRef(0);
  // The locale the cached heights/pinned were built under. Cleared inside measure()
  // (not a passive effect) so the clear lands BEFORE the write, never after it.
  const heightsLocaleRef = useRef(localeKey);

  const measure = useCallback(() => {
    if (!enabled || !footerElement) return;

    // Labels wrap differently per language, so heights cached under another locale
    // are stale. Drop them here, before measuring — `heightsRef` is additive and
    // retains collapsed items' heights, so writing the cache without this first
    // would produce a mixed-locale entry on an in-place locale switch.
    if (heightsLocaleRef.current !== localeKey) {
      heightsLocaleRef.current = localeKey;
      heightsRef.current.clear();
      footerPinnedRef.current = 0;
      renderedCollapsibleSumRef.current = 0;
    }

    const container = footerElement.closest(SIDEBAR_CONTAINER_SELECTOR);
    if (!(container instanceof HTMLElement)) return;

    const availableHeight = container.clientHeight;

    const contentBox = container.querySelector(SIDEBAR_CONTENT_SELECTOR);
    const primaryFull =
      contentBox instanceof HTMLElement ? sumChildOuterHeights(contentBox) : 0;

    // Skip the content box's ANCESTOR by `contains`, not by identity: on desktop
    // the content box sits inside the flex-1 scroll wrapper, so matching by
    // identity would add that wrapper's clamped height to chrome and double-count
    // `primaryFull` — the budget goes negative and everything always collapses.
    let chromeHeight = 0;
    let footerOuter: HTMLElement | null = null;
    for (const child of Array.from(container.children)) {
      if (!(child instanceof HTMLElement)) continue;
      if (contentBox && child.contains(contentBox)) continue;
      if (child.contains(footerElement)) {
        footerOuter = child;
        continue;
      }
      chromeHeight += outerHeight(child);
    }

    const moreEl = footerElement.querySelector(`[data-testid="${FOOTER_MORE_TEST_ID}"]`);
    // No More tile rendered (sidebar IA: footer items collapse into the primary
    // nav More instead) ⇒ nothing reserves a row inside the footer, so the split
    // must budget zero for it — reserving the tile estimate here taxes the budget
    // and lets the one-item collapse heuristic pull collapsed items back.
    const moreHeight =
      moreEl instanceof HTMLElement && moreEl.offsetHeight > 0
        ? outerHeight(moreEl)
        : isSidebarIAEnabled
          ? 0
          : FOOTER_RAIL_TILE_ESTIMATE_PX;

    let renderedCollapsibleSum = 0;
    let renderedCollapsibleCount = 0;
    for (const id of Object.keys(FOOTER_RAIL_TEST_IDS) as FooterRailId[]) {
      if (!isSidebarIAEnabled && FOOTER_RAIL_IA_ONLY_IDS.has(id)) continue;
      const testIds = FOOTER_RAIL_TEST_IDS[id];
      const selector = (typeof testIds === 'string' ? [testIds] : testIds)
        .map((testId) => `[data-testid="${testId}"]`)
        .join(',');
      const el = footerElement.querySelector(selector);
      if (el instanceof HTMLElement) {
        const height = outerHeight(el);
        if (height > 0) {
          heightsRef.current.set(id, height);
          renderedCollapsibleSum += height;
          renderedCollapsibleCount += 1;
        }
      }
    }

    // No More tile ⇒ nothing collapsed ⇒ the footer is at full extent: the only
    // pass where the pinned remainder is ground truth. Measure the wrapper, not the
    // inner element, so its padding counts.
    if (!moreEl) {
      // A tile disappeared since the last pass (collapsed) ⇒ footer shrank: keep
      // the last full-extent reserve so the freed row doesn't re-inflate the
      // budget — otherwise every additional item needs fresh window shrink to
      // fold, and a tall tile (credit usage) stalls the whole footer. Still let
      // the pass through so viewport-keyed inputs (availableHeight, ...) update.
      const sumGrew = renderedCollapsibleSum > renderedCollapsibleSumRef.current;
      renderedCollapsibleSumRef.current = renderedCollapsibleSum;
      if (sumGrew) {
        const footerFullHeight = outerHeight(footerOuter ?? footerElement);
        // Also reclaim the collapsibles' own gaps — otherwise they're counted here
        // AND again when splitFooterRail lays the collapsibles out, over-reserving
        // and collapsing an item early. Keep one gap of headroom (count − 1) so the
        // tightening can't tip into under-reserving (which would scroll the primary).
        const collapsibleGapReclaim =
          Math.max(0, renderedCollapsibleCount - 1) * FOOTER_RAIL_GAP_PX;
        footerPinnedRef.current = Math.max(
          0,
          footerFullHeight - renderedCollapsibleSum - collapsibleGapReclaim
        );
      }
    }

    const reservedHeight = chromeHeight + primaryFull + footerPinnedRef.current;
    const nextHeights = new Map(heightsRef.current);
    const next: FooterRailMeasurement = {
      availableHeight,
      reservedHeight,
      heights: nextHeights,
      moreHeight,
      hasMeasured: true,
    };

    // Cache from inside measure(), keyed by the locale it just measured — NOT from a
    // post-commit effect keyed on `localeKey`, which would re-run with the STALE
    // measurement and a zeroed pinned remainder on an in-place locale switch and
    // poison the new locale's entry. The stale-locale `heightsRef` was already dropped
    // at the top of this pass, so `next` is single-locale.
    measurementCache.set(localeKey, {
      measurement: next,
      footerPinned: footerPinnedRef.current,
    });

    setMeasurement((previous) =>
      previous.hasMeasured &&
      previous.availableHeight === availableHeight &&
      previous.reservedHeight === reservedHeight &&
      previous.moreHeight === moreHeight &&
      heightsEqual(previous.heights, nextHeights)
        ? previous
        : next
    );
  }, [enabled, footerElement, isSidebarIAEnabled, localeKey]);

  useEffect(() => {
    if (!enabled || !footerElement) return;
    const container = footerElement.closest(SIDEBAR_CONTAINER_SELECTOR);
    if (!(container instanceof HTMLElement) || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = createResizeObserverWithRAF(() => measure());
    // Viewport resize, primary items appearing, and async footer content resolving.
    observer.observe(container);
    const contentBox = container.querySelector(SIDEBAR_CONTENT_SELECTOR);
    if (contentBox instanceof HTMLElement) observer.observe(contentBox);
    observer.observe(footerElement);
    return () => observer.disconnect();
  }, [enabled, footerElement, measure]);

  return { footerRef: setFooterElement, measurement, measure };
}
